if (process.env.NODE_ENV === 'debug') require('babel-polyfill'); // eslint-disable-line

import './libs/locale';

import Rx from '@evo/rx';
import { fromJS, Map } from 'immutable';
import { tanok, createStore } from 'tanok';
import {
    Besida,
    BESIDA_ROLE_COMPANY,
    BESIDA_ROLE_BUYER
} from '@evo/chat-core';
import { init as initUserFetch } from '@evo/user-info';

import { BesidaDispatcher } from './core/dispatchers/chat';
import { WebsocketDispatcher } from './core/dispatchers/websocket';
import { AuthDispatcher } from './core/dispatchers/auth';
import { BanDispatcher } from './core/dispatchers/ban';
import { BesidaModel } from './core/model';
import { socketStoreMutation, invokeActionListeners } from './core/middlewares';
import { BesidaView } from './views';
import { tanokMountWrapper } from './views/helpers/MountWrapper';

import { Config } from './core/config';
import { collectDispatchers, isFunction } from './libs/utils';
import { getFilteredChats } from './libs/helpers';

import * as a from './libs/actions';
import * as c from './libs/constants';

const listenersMap = {
    [c.OPEN_DIALOG_ACTION_LISTENERS]: [],
    [c.UPDATE_ACTION_LISTENERS]: [],
    [c.INIT_ACTION_LISTENERS]: []
};

function genRemoveListener(callback, action) {
    return () => {
        const index = listenersMap[action].indexOf(callback);
        if (~index) listenersMap[action].splice(index, 1);
    }
}

let tanokStream = null;
let tanokStore = null;
let initedForSPA = null;

export class BesidaMobileChat {
    static instance = null;

    static userRoles = {
        company: BESIDA_ROLE_COMPANY,
        buyer: BESIDA_ROLE_BUYER,
    };

    static languages = {
        ru: c.DEFAULT_LOCALE,
        uk: c.UK_LOCALE,
    };

    constructor(initialAppData, config, userRole, bnode = null) {
        if (BesidaMobileChat.instance) return BesidaMobileChat.instance;

        new Config(config);

        const besidaModel = new BesidaModel(initialAppData, userRole);

        BesidaMobileChat.instance = this;

        // not the best thing in chat
        if (process.env.NODE_ENV === 'production') initUserFetch(config.promConfig.defaultOrigin);

        // stream-set
        const windowResizeObserver = Rx.Observable.fromEvent(window, 'resize')
            .debounce(c.RESIZE_EVENT_DEBOUNCE_DELAY)
            .map((ev) => ({
                action: a.EVENT_WINDOW_RESIZE, streamName: null,
                payload: ev
            }));

        const intervalObserver = Rx.Observable
            .interval(c.BESIDA_RERENDER_INTERVAL)
            .map(() => ({action: a.EVENT_BESIDA_RERENDER, streamName: null}));

        // tanok data
        const immModel = fromJS(besidaModel);
        const squashedDispatchers = collectDispatchers(
            new BesidaDispatcher,
            new WebsocketDispatcher,
            new AuthDispatcher,
            new BanDispatcher,
        );
        const options = {
            outerEventStream: Rx.Observable.merge(
                windowResizeObserver,
                intervalObserver),
            middlewares: [
                socketStoreMutation,
                invokeActionListeners(listenersMap)
            ]
        };

        // here comes Johny
        if (bnode) {
            options.container = bnode;
            const tanokRes = tanok(immModel, squashedDispatchers, BesidaView, options);

            tanokStream = tanokRes.tanokStream;
            tanokStore = tanokRes.store;
        } else {
            const streamStoreArr = createStore(immModel, squashedDispatchers, options);

            tanokStream = streamStoreArr[0];
            tanokStore = streamStoreArr[1];

            initedForSPA = true;
        }
    }

    // improve module; make method static, and able to push events in queue, if besida was not
    // inited yet
    dispatchEvent(action, payload) {
        if (!tanokStream) throw Error('Chat was not inited');

        if (!c.PUBLIC_ACTIONS_LIST.includes(action)) throw Error(
            `Unknown, or privet dispatcher action: ${action}`);

        tanokStream.send(action, payload);
    }

    getDispatcherActions() {
        if (!tanokStream) throw Error('Chat was not inited');

        const methodsMap = {};

        c.PUBLIC_ACTIONS_LIST.forEach((action) => {
            methodsMap[`${action}Dispatch`] = (payload) => {
                tanokStream.send(action, payload);
            };

            methodsMap[`${action}Dispatch`] = methodsMap[`${action}Dispatch`].bind(this);
        });

        return methodsMap;
    }

    get chatAppComponent() {
        if (!initedForSPA) return null;

        return tanokMountWrapper(tanokStream, tanokStore);
    }

    static get socketStore() {
        if (!Besida.instance) return Map();
        return Besida.instance && Besida.instance.store;
    }

    static get userRole() {
        if (!tanokStore) return void 2 / 0;
        const currStore = initedForSPA ? tanokStore : tanokStore.getState();

        return currStore.get('userRole');
    }

    // improve, return all chats of given status, with no role dependent data
    static filteredChats(status, role) {
        if (!Besida.instance) return [];

        const allChats = Besida.instance.store.get('chats').toJS();

        return getFilteredChats(allChats, status, role);
    }

    // listeners are static, coz we must give ability to user,
    // subscribe on actions before chat was inited
    static addInitListener(callback) {
        if (!isFunction(callback)) throw new Error('Callback should be a function');

        listenersMap[c.INIT_ACTION_LISTENERS].push(callback);

        return genRemoveListener(callback, c.INIT_ACTION_LISTENERS);
    }

    static addUpdateListener(callback) {
        if (!isFunction(callback)) throw new Error('Callback should be a function');

        listenersMap[c.UPDATE_ACTION_LISTENERS].push(callback);

        return genRemoveListener(callback, c.UPDATE_ACTION_LISTENERS);
    }

    static addOpenDialogListener(callback) {
        if (!isFunction(callback)) throw new Error('Callback should be a function');

        listenersMap[c.OPEN_DIALOG_ACTION_LISTENERS].push(callback);

        return genRemoveListener(callback, c.OPEN_DIALOG_ACTION_LISTENERS);
    }
}
